import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function ProjectCalendar({ board, groups, items, members, api, onDataChange }) {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Get current month info
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  // Filter items with due dates in current month
  const itemsThisMonth = items.filter(item => {
    const dueDate = item.column_values['due_date'];
    if (!dueDate) return false;
    const itemDate = new Date(dueDate);
    return itemDate.getMonth() === month && itemDate.getFullYear() === year;
  });

  // Group items by day
  const itemsByDay = {};
  itemsThisMonth.forEach(item => {
    const day = new Date(item.column_values['due_date']).getDate();
    if (!itemsByDay[day]) {
      itemsByDay[day] = [];
    }
    itemsByDay[day].push(item);
  });

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(year, month + direction, 1));
  };

  const renderCalendarDay = (day) => {
    const dayItems = itemsByDay[day] || [];
    const isToday = new Date().getDate() === day && 
                   new Date().getMonth() === month && 
                   new Date().getFullYear() === year;

    return (
      <div 
        key={day}
        className={`min-h-24 p-1 border border-gray-100 ${
          isToday ? 'bg-blue-50 border-blue-200' : 'bg-white'
        }`}
      >
        <div className={`text-sm font-medium mb-1 ${
          isToday ? 'text-blue-600' : 'text-gray-700'
        }`}>
          {day}
        </div>
        
        <div className="space-y-1">
          {dayItems.slice(0, 3).map((item) => {
            const group = groups.find(g => g.id === item.group_id);
            const status = item.column_values['status'] || 'Not Started';
            
            return (
              <div 
                key={item.id}
                className="text-xs p-1 rounded truncate"
                style={{ 
                  backgroundColor: group?.color + '20',
                  borderLeft: `2px solid ${group?.color || '#6B7280'}`
                }}
                title={`${item.name} - ${status}`}
              >
                {item.name}
              </div>
            );
          })}
          
          {dayItems.length > 3 && (
            <div className="text-xs text-gray-500">
              +{dayItems.length - 3} mais
            </div>
          )}
        </div>
      </div>
    );
  };

  // Create calendar grid
  const calendarDays = [];
  
  // Empty cells for days before month starts
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(<div key={`empty-${i}`} className="min-h-24 p-1 bg-gray-50" />);
  }
  
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(renderCalendarDay(day));
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{monthNames[month]} {year}</span>
            </CardTitle>
            
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigateMonth(-1)}
              >
                ←
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentDate(new Date())}
              >
                Hoje
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigateMonth(1)}
              >
                →
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Calendar Header */}
          <div className="grid grid-cols-7 gap-0 mb-2">
            {dayNames.map((day) => (
              <div key={day} className="text-center text-sm font-medium text-gray-600 p-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-0 border border-gray-200">
            {calendarDays}
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-4">
            {groups.map((group) => {
              const groupItems = itemsThisMonth.filter(item => item.group_id === group.id);
              if (groupItems.length === 0) return null;
              
              return (
                <div key={group.id} className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: group.color }}
                  />
                  <span className="text-sm text-gray-600">
                    {group.title} ({groupItems.length})
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}